import * as styles from "./MenuButton.css";
import Popconfirm, { PopconfirmProps } from "@components/feedback/Popconfirm";
import {
  ButtonProps as AntDButtonProps,
  Popover as AntDPopover,
  PopoverProps as AntDPopoverProps,
  Space,
} from "antd";
import { addKeyIfNotUndefined } from "common/utils/universal/object";
import React from "react";
import Button, { ButtonProps } from "../Button";
import { HtmlElementTypeOf } from "common/utils/universal/types";

type IconPosition = "left" | "right";
type MenuButtonProps = AntDPopoverProps & {
  ariaLabel?: string; // Accessibility label for screen readers
  iconPosition?: IconPosition; // Icon position: left or right
  icon?: React.ReactNode; // Icon element
  type?: AntDButtonProps["type"];
  shape?: AntDButtonProps["shape"];
  size?: AntDButtonProps["size"];
  block?: AntDButtonProps["block"];
  disabled?: AntDButtonProps["disabled"];
  menuButtons?: AntDPopoverProps["content"];
  dataTestId?: string;
  popoverTitle?: AntDPopoverProps["title"];
  title?: AntDButtonProps["title"];
  loading?: AntDButtonProps["loading"];
};
const MenuButton: React.FC<MenuButtonProps> = ({
  ariaLabel,
  icon,
  iconPosition = "left",
  type,
  shape,
  size,
  block,
  children,
  disabled,
  menuButtons,
  open,
  onOpenChange,
  dataTestId,
  title,
  popoverTitle,
  loading,
  ...rest
}) => {
  return (
    <AntDPopover
      {...rest}
      aria-label={ariaLabel} // Accessible label
      trigger={["click"]}
      content={menuButtons}
      arrow={false}
      placement="bottom"
      open={open}
      onOpenChange={onOpenChange}
      title={popoverTitle}
    >
      <Button
        {...addKeyIfNotUndefined("data-testid", dataTestId)}
        data-testid={dataTestId}
        disabled={disabled}
        type={type}
        shape={shape}
        size={size}
        block={block}
        title={title}
        loading={loading}
      >
        {!loading ? (
          <Space>
            {iconPosition === "left" ? icon : null}
            {children}
            {iconPosition === "right" ? icon : null}
          </Space>
        ) : null}
      </Button>
    </AntDPopover>
  );
};
export default MenuButton;

export const MenuButtonsContainer = (
  props: HtmlElementTypeOf<HTMLDivElement>
) => <div {...props} className={styles.menuButtonsContainer} />;

const MenuItemButtonBase = (props: ButtonProps) => (
  <Button {...props} className={styles.menuItemButtonBase} />
);

const MenuItemButton = ({
  children,
  dataTestId,
  ...rest
}: { children: React.ReactNode; dataTestId: string } & Omit<
  AntDButtonProps,
  "iconPosition"
>) => {
  return (
    <MenuItemButtonBase dataTestId={dataTestId} type="text" {...rest}>
      {children}
    </MenuItemButtonBase>
  );
};

const ButtonIconContainer = (props: HtmlElementTypeOf<HTMLSpanElement>) => (
  <span {...props} className={styles.buttonIconContainer} />
);

interface MenuButtonWithConfirmProps {
  type: PopconfirmProps["type"];
  title: PopconfirmProps["title"];
  description: PopconfirmProps["description"];
  onConfirm: PopconfirmProps["onConfirm"];
  onCancel: PopconfirmProps["onCancel"];
  okText: PopconfirmProps["okText"];
  cancelText: PopconfirmProps["cancelText"];
  icon?: PopconfirmProps["icon"];
  content: React.ReactNode | string;
  danger?: AntDButtonProps["danger"];
  dataTestId: string;
}
export const MenuButtonWithConfirm = ({
  type,
  title,
  description,
  onConfirm,
  onCancel,
  okText,
  cancelText,
  icon,
  content,
  danger,
  dataTestId,
}: MenuButtonWithConfirmProps) => {
  return (
    <Popconfirm
      type={type}
      title={title}
      description={description}
      onConfirm={onConfirm}
      onCancel={onCancel}
      okText={okText}
      cancelText={cancelText}
      okButtonProps={{ "data-testid": "popconfirm-confirm" }}
      cancelButtonProps={{ "data-testid": "popconfirm-cancel" }}
      destroyTooltipOnHide={true}
    >
      <MenuItemButton dataTestId={dataTestId} danger={danger}>
        <div>
          {icon ? <ButtonIconContainer>{icon}</ButtonIconContainer> : null}
          {content}
        </div>
      </MenuItemButton>
    </Popconfirm>
  );
};

interface StandardMenuButtonProps {
  danger?: AntDButtonProps["danger"];
  onClick: AntDButtonProps["onClick"];
  icon?: React.ReactNode;
  content: React.ReactNode | string;
  dataTestId: string;
}

export const StandardMenuButton = ({
  danger,
  onClick,
  icon,
  content,
  dataTestId,
}: StandardMenuButtonProps) => {
  return (
    <MenuItemButton dataTestId={dataTestId} danger={danger} onClick={onClick}>
      <div>
        {icon ? <ButtonIconContainer>{icon}</ButtonIconContainer> : null}
        {content}
      </div>
    </MenuItemButton>
  );
};

interface LinkMenuButtonProps {
  danger?: AntDButtonProps["danger"];
  onClick: AntDButtonProps["onClick"];
  icon?: React.ReactNode;
  content: React.ReactNode | string;
  dataTestId: string;
  link: HTMLAnchorElement["href"];
}

export const LinkMenuButton = ({
  danger,
  onClick,
  icon,
  content,
  link,
  dataTestId,
}: LinkMenuButtonProps) => {
  return (
    <MenuItemButton dataTestId={dataTestId} danger={danger} onClick={onClick}>
      <a
        className={styles.baseMenuLink}
        href={link}
        target="_blank"
        rel="noreferrer"
      >
        {icon ? <ButtonIconContainer>{icon}</ButtonIconContainer> : null}
        {content}
      </a>
    </MenuItemButton>
  );
};
